import React from 'react';
import { bool, func, number, object, string } from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';

import { useConfiguration } from '../../../context/configurationContext';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { formatMoney } from '../../../util/currency';
import { injectIntl, intlShape } from '../../../util/reactIntl';
import { Form, InlineTextButton, IconClear } from '../../../components';

import css from './PriceFilterForm.module.css';

const { Money } = sdkTypes;

const DEBOUNCE_WAIT_TIME = 400;

const PREDEFINED_RANGES = [
  { minPrice: 0, maxPrice: 50 },
  { minPrice: 50, maxPrice: 100 },
  { minPrice: 100, maxPrice: 150 },
  { minPrice: 150, maxPrice: 200 },
  { minPrice: 200, maxPrice: 250 },
];

/**
 * Converts a numeric amount into a Money object using the configured currency.
 *
 * @param {number} amount - The amount to convert.
 * @returns {Money} - The Money object.
 */
const createMoney = (amount, currency) => new Money(amount, currency);

// Function to convert price to correct Money format (add two decimal places)
const convertToMoneyFormat = price => (price === 0 ? 0 : price * 100);

// Function to generate a localized price range label
// If min is 0, it uses 'under' format, otherwise it uses 'range' format
const generatePriceLabel = (min, max, currency, intl) => {
  if (min === 0) {
    return intl.formatMessage(
      {
        id: 'PriceFilterForm.under',
      },
      {
        max: formatMoney(intl, createMoney(convertToMoneyFormat(max), currency)),
      }
    );
  }
  return intl.formatMessage(
    {
      id: 'PriceFilterForm.range',
    },
    {
      min: formatMoney(intl, createMoney(convertToMoneyFormat(min), currency)),
      max: formatMoney(intl, createMoney(convertToMoneyFormat(max), currency)),
    }
  );
};

const parseMin = value => {
  // If the value is an empty string, return it as is
  if (value === '') return value;

  // If it's a valid number, return the number
  const parsedValue = Number(value);
  return isNaN(parsedValue) ? value : parsedValue;
};

const parseMax = value => {
  if (value === '') return value;

  const parsedValue = Number(value);
  return isNaN(parsedValue) ? value : parsedValue;
};

/**
 * Validation function for Final Form
 * Ensures minPrice <= maxPrice
 */
const validatePriceRange = (values, intl) => {
  const errors = {};

  const minPrice = values.minPrice !== '' ? Number(values.minPrice) : '';
  const maxPrice = values.maxPrice !== '' ? Number(values.maxPrice) : '';

  if (isNaN(minPrice)) {
    errors.minPrice = intl.formatMessage({
      id: 'PriceFilterForm.invalidNumber',
    });
  }

  if (isNaN(maxPrice)) {
    errors.maxPrice = intl.formatMessage({
      id: 'PriceFilterForm.invalidNumber',
    });
  }

  if (minPrice !== '' && maxPrice !== '' && minPrice > maxPrice) {
    errors.minPrice = intl.formatMessage({ id: 'PriceFilterForm.minPriceTooHigh' });
    errors.maxPrice = intl.formatMessage({ id: 'PriceFilterForm.maxPriceTooLow' });
  }

  return errors;
};

// PriceFilterForm component
const PriceFilterFormComponent = props => {
  const { liveEdit, onChange, onSubmit, onCancel, onClear, intl, ...rest } = props;

  if (liveEdit && !onChange) {
    throw new Error('PriceFilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'PriceFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  const handleChange = debounce(
    formState => {
      if (formState.dirty) {
        const { minPrice, maxPrice, ...restValues } = formState.values;
        onChange({
          minPrice: minPrice === '' ? rest.min : minPrice,
          maxPrice: maxPrice === '' ? rest.max : maxPrice,
          ...restValues,
        });
      }
    },
    DEBOUNCE_WAIT_TIME,
    { leading: false, trailing: true }
  );

  const handleSubmit = values => {
    const { minPrice, maxPrice, ...restValues } = values;
    return onSubmit({
      minPrice: minPrice === '' ? rest.min : minPrice,
      maxPrice: maxPrice === '' ? rest.max : maxPrice,
      ...restValues,
    });
  };

  const formCallbacks = liveEdit
    ? { onSubmit: () => null }
    : { onSubmit: handleSubmit, onCancel, onClear };

  return (
    <FinalForm
      {...rest}
      {...formCallbacks}
      validate={values => validatePriceRange(values, intl)}
      render={formRenderProps => {
        const {
          form,
          handleSubmit,
          id,
          showAsPopup,
          onClear,
          onCancel,
          isOpen,
          isInSideBar,
          showCancelButton,
          contentRef,
          style,
          min,
          max,
          invalid,
          children,
        } = formRenderProps;

        const config = useConfiguration();

        const classes = classNames(css.root, {
          [css.popup]: showAsPopup,
          [css.isOpenAsPopup]: showAsPopup && isOpen,
          [css.plain]: !showAsPopup,
          [css.isOpen]: !showAsPopup && isOpen,
        });

        const handleCancel = () => {
          // reset the final form to initialValues
          form.reset();
          onCancel();
        };

        const clear = intl.formatMessage({ id: 'PriceFilterForm.clear' });
        const cancel = intl.formatMessage({ id: 'PriceFilterForm.cancel' });
        const submit = intl.formatMessage({ id: 'PriceFilterForm.submit' });

        const submitDisabled = invalid;

        return (
          <Form
            className={classes}
            onSubmit={handleSubmit}
            tabIndex="0"
            contentRef={contentRef}
            style={{ minWidth: '300px', ...style }}
          >
            <div className={css.contentWrapper}>
              <div className={css.predefinedButtonsWrapper}>
                {PREDEFINED_RANGES.map(range => {
                  const priceLabel = generatePriceLabel(
                    range.minPrice,
                    range.maxPrice,
                    config.currency,
                    intl
                  );
                  return (
                    <InlineTextButton
                      key={range.minPrice}
                      className={css.predefinedButton}
                      type="button"
                      onClick={() => {
                        form.change('minPrice', range.minPrice);
                        form.change('maxPrice', range.maxPrice);
                        handleSubmit({ minPrice: range.minPrice, maxPrice: range.maxPrice });
                      }}
                    >
                      {priceLabel}
                    </InlineTextButton>
                  );
                })}
              </div>

              <div className={css.inputsWrapper}>
                <div className={css.inputWrapper}>
                  <span className={css.priceUnit}>
                    {intl.formatMessage({
                      id: 'PriceFilterForm.minPriceUnit',
                    })}
                  </span>
                  <Field
                    className={classNames(css.minPrice, { [css.priceInSideBar]: isInSideBar })}
                    id={`${id}.minPrice`}
                    name="minPrice"
                    component="input"
                    type="number"
                    placeholder={min}
                    parse={parseMin}
                  />
                </div>
                <div className={css.inputWrapper}>
                  <span className={css.priceUnit}>
                    {intl.formatMessage({
                      id: 'PriceFilterForm.maxPriceUnit',
                    })}
                  </span>
                  <Field
                    className={classNames(css.maxPrice, { [css.priceInSideBar]: isInSideBar })}
                    id={`${id}.maxPrice`}
                    name="maxPrice"
                    component="input"
                    type="number"
                    placeholder={max}
                    parse={parseMax}
                  />
                </div>
                <button className={css.submitButton} type="submit" disabled={submitDisabled}>
                  {submit}
                </button>
              </div>
            </div>

            {liveEdit ? (
              <>
                <FormSpy onChange={handleChange} subscription={{ values: true, dirty: true }} />
                {children}
              </>
            ) : (
              <div className={css.buttonsWrapper}>
                {showCancelButton ? (
                  <button className={css.cancelButton} type="button" onClick={handleCancel}>
                    {cancel}
                  </button>
                ) : null}
                <button className={css.clearButton} type="button" onClick={onClear}>
                  <IconClear className={css.clearIcon} pathClassName={css.clearIconPath} />
                  {clear}
                </button>
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

PriceFilterFormComponent.defaultProps = {
  liveEdit: false,
  showAsPopup: false,
  showCancelButton: true,
  isOpen: false,
  contentRef: null,
  style: null,
  min: 0,
  step: 1,
  onCancel: null,
  onChange: null,
  onClear: null,
  onSubmit: null,
};

PriceFilterFormComponent.propTypes = {
  id: string.isRequired,
  liveEdit: bool,
  showAsPopup: bool,
  showCancelButton: bool,
  onCancel: func,
  onChange: func,
  onClear: func,
  onSubmit: func,
  isOpen: bool,
  contentRef: func,
  style: object,
  min: number.isRequired,
  max: number.isRequired,
  step: number,
  intl: intlShape.isRequired,
};

const PriceFilterForm = injectIntl(PriceFilterFormComponent);

export default PriceFilterForm;
